import { Component, OnInit, ViewChild } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ShopifyService } from '../../../shared/services/shopify.service';
import { BehaviorSubject, from } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Features, Image, Product, Variant } from '../../../shared/model/store-product-details.model';
import { ErrorResponse } from '../../../shared/model/store.models';
import { SlickCarouselComponent } from 'ngx-slick-carousel';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../../../environments/environment';
import GaService from '../../../shared/services/ga.service';
import ClearcalsIcons from 'app/icons/icons.data';

@Component({
  selector: 'clearcals-product',
  templateUrl: './product.component.html',
  styleUrls: ['./product.component.scss']
})
export class ProductComponent implements OnInit {
  productDetails$ = new BehaviorSubject<Product | null>(null);
  error$ = new BehaviorSubject<ErrorResponse | null>(null);
  selectedImage$ = new BehaviorSubject<Image | null>(null);
  selectedVariant$ = new BehaviorSubject<Variant | null>(null);
  isLoading$ = new BehaviorSubject<boolean>(false);
  hintPremiumOffer$ = new BehaviorSubject<string | null>(null);
  shippingInfo$ = new BehaviorSubject<string | null>(null);
  heroContent$ = new BehaviorSubject<string | null>(null);
  features$ = new BehaviorSubject<Features | null>(null)
  whatsappIcon = ClearcalsIcons.getIcon('whatsapp');

  @ViewChild('slickModal') slickModal: SlickCarouselComponent;
  @ViewChild('collectionSlickModal') collectionSlickModal: SlickCarouselComponent;

  slideConfig = {
    arrows: true,
    dots: false,
    infinite: false,
  };

  constructor(
    private shopifyService: ShopifyService,
    private route: ActivatedRoute,
    private _snackBar: MatSnackBar,
    private gaService: GaService,
    private router: Router,
    private meta: Meta,
    private titleService: Title
  ) { }

  ngOnInit(): void {
    this.route.fragment.subscribe((_) => {
      this.slickModal?.slickGoTo(0);
    });

    this.route.url.subscribe(urlSegments => {
      const handleSegment = urlSegments[urlSegments.length - 1];
      this.isLoading$.next(true);
      if (handleSegment.path) {
        from(this.shopifyService.getProductDetailsByHandle(handleSegment.path)).subscribe((response) => {
          if (response) {
            if ('errors' in response) {
              this.error$.next(response);
              this.error$.subscribe((error) => {
                if (error) {
                  this._snackBar.open(error.errors[0].message, 'Close', {
                    panelClass: ['mat-toolbar', 'mat-warn'],
                  });
                }
              });
            } else {
              if (response.product === null) {
                window.location.href = "/error";
              }

              this.productDetails$.next(response.product as Product);

              if (this.productDetails$.value?.images.edges.length) {
                this.selectedImage$.next(this.productDetails$.value?.images.edges[0]);
              }

              if (this.productDetails$.value?.variants.edges.length) {
                const selectedVariant = this.productDetails$.value?.variants.edges[0];
                this.selectedVariant$.next(selectedVariant);
                this.addScriptsToHead(this.productDetails$.value, selectedVariant.node.title);

                this.initializeWidget(+selectedVariant.node.price.amount);
              }

              this.productDetails$.value?.metafields.forEach((metafield) => {
                if (metafield?.key === 'hint_premium_offer') {
                  this.hintPremiumOffer$.next(metafield.value);
                }
                if (metafield?.key === 'shipping_info') {
                  this.shippingInfo$.next(metafield.value);
                }
                if (metafield?.key === 'hero_content') {
                  this.heroContent$.next(JSON.parse(metafield.value).heroContent);
                }
                if (metafield?.key === "features") {
                  this.features$.next(JSON.parse(metafield.value).sections);
                }
              });
            }

            this.isLoading$.next(false);
          }
        });
      }
    });

    this.selectedVariant$.subscribe((variant) => {
      if (variant && this.productDetails$.value) {
        this.updateMetaTags(this.productDetails$.value, variant);
      }
    });
  }

  changeImage(image: Image) {
    this.selectedImage$.next(image);
  }

  selectVariant(variant: Variant) {
    this.selectedVariant$.next(variant);
  }

  clickBuyNow() {
    this.gaService.gaEventFun({
      action: 'buy_now_click',
      label: this.router.url,
    });
  }

  async checkout(variant: Variant) {
    this.isLoading$.next(true);
    const data = await this.shopifyService.checkout(variant.node.id);
    if (data) {
      if ('errors' in data) {
        this.error$.next(data);
        this.error$.subscribe((error) => {
          if (error) {
            this._snackBar.open(error.errors[0].message, 'Close', {
              panelClass: ['mat-toolbar', 'mat-warn'],
            });
          }
        });
      } else {
        const checkoutData = data;

        if (checkoutData.cartCreate.userErrors.length) {
          this._snackBar.open(checkoutData.cartCreate.userErrors[0].message, 'Close', {
          });
        } else {
          // validate if the variant is added to the cart
          const noOfItems = checkoutData.cartCreate.cart.lines.edges.length;
          // check if variant.node.id matches with one of the items in the cart
          checkoutData.cartCreate.cart.lines.edges.forEach((line, index) => {
            if (line.node.merchandise.id === variant.node.id && noOfItems === 2) {
              window.location.href = checkoutData.cartCreate.cart.checkoutUrl;
            } else if (index === noOfItems - 1) {
              this._snackBar.open('The item is not added to the cart.', 'Close', {
                panelClass: ['mat-toolbar', 'mat-warn'],
              });
            }
          });
        }
      }
    }
    this.isLoading$.next(false);
  }

  // structured data for product
  addScriptsToHead(product: Product, description: string): void {
    const head = document.getElementsByTagName('head')[0];
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    const article = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      "name": product.title,
      "image": product.images.edges[0].node.url,
      "description": description,
      "brand": {
        "@type": "Brand",
        "name": "Garmin"
      },
      "sku": "GV-5-001",
      "mpn": "010-02788-00",
      "offers": [
        {
          "@type": "Offer",
          "url": `${environment.appBaseUrl}/store/garmin-watches/${product.handle}`,
          "priceCurrency": "INR",
          "price": +product.variants.edges[0].node.price.amount,
          "availability": "https://schema.org/InStock",
          "shippingDetails": {
            "@type": "OfferShippingDetails",
            "shippingRate": {
              "@type": "MonetaryAmount",
              "currency": "INR",
              "value": 0.00
            },
            "shippingDestination": {
              "@type": "DefinedRegion",
              "addressCountry": "IN"
            },
            "deliveryTime": {
              "@type": "ShippingDeliveryTime",
              "businessDays": {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": [
                  "https://schema.org/Monday",
                  "https://schema.org/Tuesday",
                  "https://schema.org/Wednesday",
                  "https://schema.org/Thursday",
                  "https://schema.org/Friday"
                ]
              },
              "handlingTime": {
                "@type": "QuantitativeValue",
                "minValue": 1,
                "maxValue": 2,
                "unitCode": "DAY"
              },
              "transitTime": {
                "@type": "QuantitativeValue",
                "minValue": 4,
                "maxValue": 7,
                "unitCode": "DAY"
              }
            }
          },
          "hasMerchantReturnPolicy": {
            "@type": "MerchantReturnPolicy",
            "applicableCountry": "IN",
            "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
            "merchantReturnDays": 1,
            "returnMethod": "https://schema.org/ReturnByMail",
            "returnFees": "https://schema.org/FreeReturn"
          }
        },
        {
          "@type": "Offer",
          "name": `${product.title} + Hint Premium 1-Month Subscription`,
          "url": `${environment.appBaseUrl}/store/garmin-watches/${product.handle}`,
          "priceCurrency": "INR",
          "price": +product.variants.edges[0].node.price.amount,
          "availability": "https://schema.org/InStock",
          "shippingDetails": {
            "@type": "OfferShippingDetails",
            "shippingRate": {
              "@type": "MonetaryAmount",
              "currency": "INR",
              "value": 0.00
            },
            "shippingDestination": {
              "@type": "DefinedRegion",
              "addressCountry": "IN"
            },
            "deliveryTime": {
              "@type": "ShippingDeliveryTime",
              "businessDays": {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": [
                  "https://schema.org/Monday",
                  "https://schema.org/Tuesday",
                  "https://schema.org/Wednesday",
                  "https://schema.org/Thursday",
                  "https://schema.org/Friday"
                ]
              },
              "handlingTime": {
                "@type": "QuantitativeValue",
                "minValue": 1,
                "maxValue": 2,
                "unitCode": "DAY"
              },
              "transitTime": {
                "@type": "QuantitativeValue",
                "minValue": 4,
                "maxValue": 7,
                "unitCode": "DAY"
              }
            }
          },
          "itemOffered": {
            "@type": "Product",
            "name": "Hint Premium 1-Month Subscription",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": 4.5,
              "reviewCount": 120
            }
          },
        }
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": 4.5,
        "reviewCount": 120
      }
    };
    script.innerHTML = JSON.stringify(article);
    head.insertBefore(script, head.firstChild);
  }

  updateMetaTags(product: Product, selectedVariant: Variant): void {
    const head = document.getElementsByTagName('head')[0];
    const price = parseFloat(product.variants.edges[0].node.price.amount).toFixed(0);

    this.titleService.setTitle(product.title);
    this.meta.updateTag({ name: 'description', content: product.description });

    const metaTags = [
      { name: 'og:title', content: `${product.title} | ₹ ${price}` },
      { name: 'og:image', content: product.images.edges[0].node.url },
      { name: 'og:description', content: product.description },
      { name: 'product:price:amount', content: product.variants.edges[0].node.price.amount },
      { name: 'product:price:currency', content: 'INR' },
    ];

    const twitterTags = [
      { name: 'twitter:url', content: window.location.href },
      { name: 'twitter:title', content: `${product.title} | ₹ ${price}` },
      { name: 'twitter:image', content: product.images.edges[0].node.url },
      { name: 'twitter:description', content: product.description },
    ];

    metaTags.forEach(tag => {
      let element = document.querySelector(`meta[property='${tag.name}']`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', tag.name);
        head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });

    twitterTags.forEach(tag => {
      let element = document.querySelector(`meta[name='${tag.name}']`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', tag.name);
        head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });
  }

  extractImageAlt(imageUrl: string): string {
    const lastPath = imageUrl.split('/').pop(); // Extract the last path
    if (!lastPath) return '';
    return lastPath.replace(/_/g, ' ').replace(/\.[^/.]+$/, ''); // Replace underscores and remove extension
  }

  initializeWidget(price: number): void {
    const script = document.createElement('script');
    document.head.appendChild(script);
    script.onload = () => {
      const key = environment.razorpayKey;
      const amount = price * 100; //in paise

      const widgetConfig = {
        "key": key,
        "amount": amount,
      };
      // Ensure RazorpayAffordabilitySuite is globally available or imported
      const rzpAffordabilitySuite = new (window as any).RazorpayAffordabilitySuite(widgetConfig);
      const targetElement = document.getElementById('razorpay-affordability-widget');
      if (targetElement) {
        rzpAffordabilitySuite.render(targetElement);
      } else {
        console.error('Target element for Razorpay Affordability Suite not found');
      }
    };
    script.src = 'https://cdn.razorpay.com/widgets/affordability/affordability.js';
  }
}
